/**
 * Premium Lightning Interactions
 * Enhanced UI/UX with scroll animations, lazy loading, and micro-interactions
 */

(function() {
  'use strict';

  // Hero entrance: run staggered content animation on load (above-the-fold hero)
  const initHeroEntrance = () => {
    const heroContent = document.querySelector('[data-hero-entrance]');
    if (!heroContent || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const runEntrance = () => {
      heroContent.querySelectorAll('.hero-entrance').forEach((el) => {
        el.classList.add('hero-entrance--visible');
      });
    };

    if (document.readyState === 'complete') {
      requestAnimationFrame(() => setTimeout(runEntrance, 80));
    } else {
      window.addEventListener('load', () => setTimeout(runEntrance, 80));
    }
  };

  // Intersection Observer for scroll animations (exclude hero-entrance so hero uses initHeroEntrance)
  const initScrollAnimations = () => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe elements with animation classes (not hero-entrance, they use initHeroEntrance)
    document.querySelectorAll('.fade-in-up, .fade-in, .scale-in').forEach((el) => {
      if (!el.classList.contains('hero-entrance')) observer.observe(el);
    });
  };

  // Lazy load images with blur-up effect
  const initLazyLoading = () => {
    const lazyImages = document.querySelectorAll('img[data-src]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.getAttribute('data-src');
            const srcset = img.getAttribute('data-srcset');
            
            if (src) {
              img.src = src;
              img.removeAttribute('data-src');
            }
            if (srcset) {
              img.srcset = srcset;
              img.removeAttribute('data-srcset');
            }
            
            img.classList.add('loaded');
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      lazyImages.forEach((img) => imageObserver.observe(img));
    } else {
      // Fallback for older browsers
      lazyImages.forEach((img) => {
        img.src = img.getAttribute('data-src');
        img.classList.add('loaded');
      });
    }
  };

  // Smooth scroll for anchor links
  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href !== '#' && href.length > 1) {
          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      });
    });
  };

  // Parallax effect for hero sections
  const initParallax = () => {
    const parallaxElements = document.querySelectorAll('.parallax');
    
    if (parallaxElements.length === 0) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      parallaxElements.forEach((el) => {
        const speed = parseFloat(el.dataset.parallaxSpeed || 0.5);
        const yPos = -(scrollTop * speed);
        el.style.transform = `translate3d(0, ${yPos}px, 0)`;
      });
    };

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  };

  // Enhanced hover effects for cards
  const initCardInteractions = () => {
    const cards = document.querySelectorAll('.featured-product-card');
    
    cards.forEach((card) => {
      card.addEventListener('mouseenter', function() {
        this.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
      });
      
      card.addEventListener('mousemove', function(e) {
        const rect = this.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 10;
        const rotateY = (centerX - x) / 10;
        
        this.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px)`;
        this.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
      });
      
      card.addEventListener('mouseleave', function() {
        this.style.transform = '';
        this.style.boxShadow = '';
      });
    });
  };

  // Image zoom on hover
  const initImageZoom = () => {
    const imageContainers = document.querySelectorAll('.image-zoom-container');
    
    imageContainers.forEach((container) => {
      const img = container.querySelector('img');
      if (!img) return;
      
      container.addEventListener('mousemove', function(e) {
        const rect = this.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const moveX = (x - centerX) / 10;
        const moveY = (y - centerY) / 10;
        
        img.style.transform = `scale(1.1) translate(${moveX}px, ${moveY}px)`;
      });
      
      container.addEventListener('mouseleave', function() {
        img.style.transform = '';
      });
    });
  };

  // Button ripple effect
  const initRippleEffect = () => {
    const buttons = document.querySelectorAll('.ripple-effect');
    
    buttons.forEach((button) => {
      button.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.classList.add('ripple');
        
        this.appendChild(ripple);
        
        setTimeout(() => {
          ripple.remove();
        }, 600);
      });
    });
  };

  // Loading skeleton for better perceived performance
  const initSkeletonLoader = () => {
    const skeletonElements = document.querySelectorAll('.skeleton');
    
    skeletonElements.forEach((el) => {
      el.classList.add('skeleton-loading');
    });
  };

  // Scroll progress indicator
  const initScrollProgress = () => {
    // Create progress bar
    const progressBar = document.createElement('div');
    progressBar.id = 'scroll-progress-bar';
    progressBar.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      height: 3px;
      background: linear-gradient(to right, #666, #000);
      width: 0%;
      z-index: 9999;
      transition: width 0.1s ease-out;
      pointer-events: none;
    `;
    document.body.appendChild(progressBar);

    // Create back to top button
    const backToTop = document.createElement('button');
    backToTop.id = 'back-to-top';
    backToTop.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 14L12 9L17 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    backToTop.setAttribute('aria-label', 'Back to top');
    backToTop.style.cssText = `
      position: fixed;
      bottom: 2rem;
      right: 2rem;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.8);
      color: #fff;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 9998;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    `;
    document.body.appendChild(backToTop);

    // Update progress bar
    const updateProgress = () => {
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (window.scrollY / windowHeight) * 100;
      progressBar.style.width = scrolled + '%';

      // Show/hide back to top button
      if (window.scrollY > 400) {
        backToTop.style.opacity = '1';
        backToTop.style.visibility = 'visible';
        backToTop.style.transform = 'translateY(0)';
      } else {
        backToTop.style.opacity = '0';
        backToTop.style.visibility = 'hidden';
        backToTop.style.transform = 'translateY(20px)';
      }
    };

    // Scroll to top functionality
    backToTop.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });

    // Hover effect for back to top
    backToTop.addEventListener('mouseenter', function() {
      this.style.background = 'rgba(0, 0, 0, 0.95)';
      this.style.transform = 'translateY(-4px)';
      this.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3)';
    });

    backToTop.addEventListener('mouseleave', function() {
      this.style.background = 'rgba(0, 0, 0, 0.8)';
      this.style.transform = 'translateY(0)';
      this.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
    });

    // Throttle scroll events
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateProgress();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Initial update
    updateProgress();
  };

  // Quick View Modal
  const initQuickViewModal = () => {
    if (window.__quickViewInitialized) return;
    window.__quickViewInitialized = true;

    const modal = document.getElementById('quick-view-modal');
    const content = document.getElementById('quick-view-content');
    const closeButtons = document.querySelectorAll('[data-quick-view-close]');

    if (!modal || !content) return;

    // Close modal function
    const closeModal = () => {
      modal.classList.remove('active');
      document.body.style.overflow = '';
      modal.setAttribute('aria-hidden', 'true');
    };

    // Open modal function
    const openModal = (productUrl) => {
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      modal.setAttribute('aria-hidden', 'false');
      
      // Show loading state
      content.innerHTML = `
        <div class="quick-view-modal__loading">
          <div class="quick-view-modal__spinner"></div>
          <p>Loading product...</p>
        </div>
      `;

      // Fetch product data
      fetch(productUrl + '.js')
        .then(response => response.json())
        .then(product => {
          renderProductModal(product);
        })
        .catch(error => {
          console.error('Error loading product:', error);
          content.innerHTML = `
            <div class="quick-view-modal__loading">
              <p>Error loading product. Please try again.</p>
              <a href="${productUrl}" class="quick-view-modal__view-full">View Full Page</a>
            </div>
          `;
        });
    };

    // Render product in modal
    const renderProductModal = (product) => {
      const selectedVariant = product.variants[0];
      const mainImage = product.featured_image || product.images[0] || '';
      
      // Build images HTML
      let imagesHTML = '';
      if (product.images && product.images.length > 0) {
        const mainImageHTML = `
          <div class="quick-view-modal__image-wrapper">
            <img src="${mainImage}" alt="${product.title}" class="quick-view-modal__image" id="quick-view-main-image">
          </div>
        `;
        
        let thumbnailsHTML = '<div class="quick-view-modal__thumbnail-grid">';
        product.images.slice(0, 4).forEach((image, index) => {
          thumbnailsHTML += `
            <div class="quick-view-modal__thumbnail ${index === 0 ? 'active' : ''}" 
                 data-image-index="${index}"
                 onclick="document.getElementById('quick-view-main-image').src='${image}'; 
                          document.querySelectorAll('.quick-view-modal__thumbnail').forEach(t => t.classList.remove('active')); 
                          this.classList.add('active');">
              <img src="${image}" alt="${product.title}">
            </div>
          `;
        });
        thumbnailsHTML += '</div>';
        
        imagesHTML = mainImageHTML + thumbnailsHTML;
      }

          // Build options HTML
          let optionsHTML = '';
          if (product.options && product.options.length > 0) {
            product.options.forEach((option, optionIndex) => {
              const optionKey = option.name.toLowerCase().replace(/\s+/g, '_');
              optionsHTML += `
                <div class="quick-view-modal__option">
                  <label class="quick-view-modal__option-label">${option.name}</label>
                  <div class="quick-view-modal__option-values" data-option-index="${optionIndex}" data-option-name="${optionKey}">
                    ${option.values.map(value => {
                      const variant = product.variants.find(v => {
                        const v1 = v.option1?.toLowerCase().trim();
                        const v2 = v.option2?.toLowerCase().trim();
                        const v3 = v.option3?.toLowerCase().trim();
                        const val = value.toLowerCase().trim();
                        return v1 === val || v2 === val || v3 === val;
                      });
                      const isSelected = selectedVariant[`option${optionIndex + 1}`]?.toLowerCase().trim() === value.toLowerCase().trim();
                      const isAvailable = variant && variant.available;
                      const safeValue = value.replace(/'/g, "\\'");
                      const variantsJson = JSON.stringify(product.variants);
                      return `
                        <button class="quick-view-modal__option-value ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}"
                                data-option-value="${safeValue}"
                                data-variants-json="${variantsJson.replace(/"/g, '&quot;')}"
                                ${!isAvailable ? 'disabled' : ''}
                                onclick="selectVariantOptionFromButton(this, ${optionIndex}, '${safeValue}')">
                          ${value}
                        </button>
                      `;
                    }).join('')}
                  </div>
                </div>
              `;
            });
          }

      // Build stock status
      let stockStatusHTML = '';
      if (!selectedVariant.available) {
        stockStatusHTML = '<span class="quick-view-modal__stock-status out-of-stock">Out of Stock</span>';
      } else if (selectedVariant.inventory_quantity < 10) {
        stockStatusHTML = '<span class="quick-view-modal__stock-status low-stock">Low Stock</span>';
      } else {
        stockStatusHTML = '<span class="quick-view-modal__stock-status in-stock">In Stock</span>';
      }

      // Build price HTML
      const priceHTML = selectedVariant.compare_at_price > selectedVariant.price
        ? `
          <div class="quick-view-modal__price">
            <span>${formatMoney(selectedVariant.price)}</span>
            <span class="quick-view-modal__price-compare">${formatMoney(selectedVariant.compare_at_price)}</span>
          </div>
        `
        : `<div class="quick-view-modal__price">${formatMoney(selectedVariant.price)}</div>`;

      // Render full content
      content.innerHTML = `
        <div class="quick-view-modal__images">
          ${imagesHTML}
        </div>
        <div class="quick-view-modal__info">
          <div class="quick-view-modal__brand">${product.vendor || ''}</div>
          <h2 class="quick-view-modal__title">${product.title}</h2>
          ${priceHTML}
          ${stockStatusHTML}
          ${product.description ? `<p class="quick-view-modal__description">${product.description.substring(0, 200)}${product.description.length > 200 ? '...' : ''}</p>` : ''}
          <div class="quick-view-modal__options" data-product-id="${product.id}" data-selected-variant="${selectedVariant.id}">
            ${optionsHTML}
          </div>
          <div class="quick-view-modal__quantity">
            <label class="quick-view-modal__quantity-label">Quantity</label>
            <div class="quick-view-modal__quantity-controls">
              <button class="quick-view-modal__quantity-btn" onclick="decreaseQuantity()" disabled>-</button>
              <input type="number" class="quick-view-modal__quantity-input" value="1" min="1" id="quick-view-quantity">
              <button class="quick-view-modal__quantity-btn" onclick="increaseQuantity()">+</button>
            </div>
          </div>
          <div class="quick-view-modal__actions">
            <button class="quick-view-modal__add-to-cart" 
                    onclick="addToCartFromQuickView()"
                    ${!selectedVariant.available ? 'disabled' : ''}
                    data-variant-id="${selectedVariant.id}">
              ${selectedVariant.available ? 'Add to Cart' : 'Sold Out'}
            </button>
            <a href="${product.url}" class="quick-view-modal__view-full">View Full Product Page</a>
          </div>
        </div>
      `;

      // Set up quantity controls
      const quantityInput = document.getElementById('quick-view-quantity');
      if (quantityInput) {
        quantityInput.addEventListener('input', function() {
          const qty = parseInt(this.value) || 1;
          document.querySelector('.quick-view-modal__quantity-btn:first-child').disabled = qty <= 1;
        });
      }
    };

    // Format money helper
    const formatMoney = (cents) => {
      if (!window.Shopify || !window.Shopify.formatMoney) {
        return '$' + (cents / 100).toFixed(2);
      }
      return window.Shopify.formatMoney(cents);
    };

    // Global functions for variant selection
    window.selectVariantOptionFromButton = (button, optionIndex, value) => {
      // Get variants from first button (they all have the same variants)
      const firstButton = document.querySelector('.quick-view-modal__option-value');
      const variantsJson = firstButton?.dataset.variantsJson?.replace(/&quot;/g, '"');
      if (!variantsJson) return;
      
      const variants = JSON.parse(variantsJson);
      
      // Remove selected class from all options in this group
      document.querySelectorAll(`[data-option-index="${optionIndex}"] .quick-view-modal__option-value`).forEach(btn => {
        btn.classList.remove('selected');
      });
      
      // Add selected class to clicked option
      button.classList.add('selected');
      
      // Find matching variant
      const selectedOptions = {};
      document.querySelectorAll('.quick-view-modal__option-values').forEach((container, idx) => {
        const selected = container.querySelector('.selected');
        if (selected) {
          const optionValue = selected.dataset.optionValue;
          selectedOptions[`option${idx + 1}`] = optionValue;
        }
      });
      
      const matchingVariant = variants.find(v => {
        const v1 = (v.option1 || '').toLowerCase().trim();
        const v2 = (v.option2 || '').toLowerCase().trim();
        const v3 = (v.option3 || '').toLowerCase().trim();
        const o1 = (selectedOptions.option1 || '').toLowerCase().trim();
        const o2 = (selectedOptions.option2 || '').toLowerCase().trim();
        const o3 = (selectedOptions.option3 || '').toLowerCase().trim();
        
        return (!o1 || v1 === o1) &&
               (!o2 || v2 === o2) &&
               (!o3 || v3 === o3);
      });
      
      if (matchingVariant) {
        window.updateVariantInfo(matchingVariant);
      }
    };

    window.updateVariantInfo = (variant) => {
      const container = document.querySelector('.quick-view-modal__options');
      container.dataset.selectedVariant = variant.id;
      
      const addToCartBtn = document.querySelector('.quick-view-modal__add-to-cart');
      addToCartBtn.dataset.variantId = variant.id;
      addToCartBtn.disabled = !variant.available;
      addToCartBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      
      const priceEl = document.querySelector('.quick-view-modal__price');
      if (variant.compare_at_price > variant.price) {
        priceEl.innerHTML = `
          <span>${formatMoney(variant.price)}</span>
          <span class="quick-view-modal__price-compare">${formatMoney(variant.compare_at_price)}</span>
        `;
      } else {
        priceEl.innerHTML = formatMoney(variant.price);
      }
      
      const stockStatus = document.querySelector('.quick-view-modal__stock-status');
      if (!variant.available) {
        stockStatus.className = 'quick-view-modal__stock-status out-of-stock';
        stockStatus.textContent = 'Out of Stock';
      } else if (variant.inventory_quantity < 10) {
        stockStatus.className = 'quick-view-modal__stock-status low-stock';
        stockStatus.textContent = 'Low Stock';
      } else {
        stockStatus.className = 'quick-view-modal__stock-status in-stock';
        stockStatus.textContent = 'In Stock';
      }
      
      // Update main image if variant has different image
      if (variant.featured_image) {
        const mainImage = document.getElementById('quick-view-main-image');
        if (mainImage) {
          mainImage.src = variant.featured_image;
        }
      }
    };

    window.decreaseQuantity = () => {
      const input = document.getElementById('quick-view-quantity');
      if (input && parseInt(input.value) > 1) {
        input.value = parseInt(input.value) - 1;
        document.querySelector('.quick-view-modal__quantity-btn:first-child').disabled = parseInt(input.value) <= 1;
      }
    };

    window.increaseQuantity = () => {
      const input = document.getElementById('quick-view-quantity');
      if (input) {
        input.value = parseInt(input.value) + 1;
        document.querySelector('.quick-view-modal__quantity-btn:first-child').disabled = false;
      }
    };

    window.addToCartFromQuickView = () => {
      const container = document.querySelector('.quick-view-modal__options');
      const variantId = container.dataset.selectedVariant;
      const quantity = parseInt(document.getElementById('quick-view-quantity').value) || 1;
      const addToCartBtn = document.querySelector('.quick-view-modal__add-to-cart');
      
      if (!variantId) return;
      
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Adding...';
      
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      })
      .then(response => response.json())
      .then(item => {
        addToCartBtn.textContent = 'Added to Cart!';
        addToCartBtn.style.background = '#4caf50';
        
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        setTimeout(() => {
          closeModal();
        }, 1000);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        addToCartBtn.textContent = 'Error. Try again.';
        addToCartBtn.style.background = '#f44336';
        
        setTimeout(() => {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'Add to Cart';
          addToCartBtn.style.background = '#000';
        }, 2000);
      });
    };

    // Delegated listener: works for buttons added later via JS rendering.
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-view-product]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      const productUrl = trigger.dataset.quickViewProduct;
      if (productUrl) openModal(productUrl);
    }, true);

    closeButtons.forEach(button => {
      button.addEventListener('click', closeModal);
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        closeModal();
      }
    });

    // Close on overlay click
    modal.querySelector('.quick-view-modal__overlay')?.addEventListener('click', closeModal);
  };

  // Expose for sections that re-render card markup dynamically.
  window.initQuickViewModal = initQuickViewModal;

  // Stock availability indicators
  const initStockIndicators = () => {
    const productCards = document.querySelectorAll('.lighting-product-card, .arrival-product-card');
    
    productCards.forEach(card => {
      const stockBadge = card.querySelector('.stock-badge');
      if (!stockBadge) return;

      const stockStatus = stockBadge.dataset.stock || 'in-stock';
      const stockBadgeEl = stockBadge;

      // Update badge appearance based on stock status
      if (stockStatus === 'low-stock') {
        stockBadgeEl.style.background = '#f4f4f4';
        stockBadgeEl.style.color = '#111';
        stockBadgeEl.style.border = '1px solid #d9d9d9';
        stockBadgeEl.style.animation = 'none';
      } else if (stockStatus === 'out-of-stock') {
        stockBadgeEl.style.background = '#ececec';
        stockBadgeEl.style.color = '#666';
        stockBadgeEl.style.border = '1px solid #d0d0d0';
        stockBadgeEl.style.opacity = '0.7';
      } else {
        stockBadgeEl.style.background = '#111';
        stockBadgeEl.style.color = '#fff';
        stockBadgeEl.style.border = '1px solid #111';
        stockBadgeEl.style.opacity = '1';
      }
    });
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initHeroEntrance();
      initScrollAnimations();
      initLazyLoading();
      initSmoothScroll();
      initParallax();
      initCardInteractions();
      initImageZoom();
      initRippleEffect();
      initSkeletonLoader();
      initScrollProgress();
      initStockIndicators();
      initQuickViewModal();
    });
  } else {
    initHeroEntrance();
    initScrollAnimations();
    initLazyLoading();
    initSmoothScroll();
    initParallax();
    initCardInteractions();
    initImageZoom();
    initRippleEffect();
    initSkeletonLoader();
    initScrollProgress();
    initStockIndicators();
    initQuickViewModal();
  }
})();

